import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { ArrowUpRight, ArrowDownLeft, Copy, QrCode, Wallet, ArrowLeft, Link, Plus, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { WalletConnectModal } from './WalletConnectModal';
import { TokenDetails } from './TokenDetails';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import { AddTokenModal } from './AddTokenModal';
import { TransModal } from './TransModal';
import { i18n, tb } from '../utils/i18n';
import { tr } from '../utils/localeFormat';

interface WalletTransferProps {
  user: User;
  transactions: Transaction[];
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
}

interface Token {
  symbol: string;
  name: string;
  balance: number;
  value: number;
  change: number;
  icon: string;
}

export function WalletTransfer({ user, transactions, onUpdateBalance, onAddTransaction }: WalletTransferProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [activeView, setActiveView] = useState('main');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWalletConnect, setShowWalletConnect] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeTab, setActiveTab] = useState('crypto');
  const [balanceView, setBalanceView] = useState('vttn');
  
  // 재단 지갑 전송 관련 상태
  const [isFoundationMode, setIsFoundationMode] = useState(false); // 기본값을 false로 변경
  const [transferMode, setTransferMode] = useState('individual');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [foundationAmount, setFoundationAmount] = useState('');
  
  // 새로운 지갑 선택 관련 상태
  const [senderWallet, setSenderWallet] = useState(null); // 보내는 지갑
  const [receiverWallet, setReceiverWallet] = useState(null); // 받는 지갑
  const [senderBalance, setSenderBalance] = useState(0); // 보내는 지갑 잔고
  const [receiverBalance, setReceiverBalance] = useState(0); // 받는 지갑 잔고
  const [searchQuery, setSearchQuery] = useState(''); // 검색어
  const [filteredUsers, setFilteredUsers] = useState([]); // 필터된 사용자 목록
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showAddToken, setShowAddToken] = useState(false);
  const [customTokens, setCustomTokens] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // 전송 모달 관련 상태
  const [showTransModal, setShowTransModal] = useState(false);
  
  // Swap/Buy view states - moved to top level to avoid conditional hooks
  const [fromToken, setFromToken] = useState('USDT');
  const [toToken, setToToken] = useState('VTTN');
  const [swapAmount, setSwapAmount] = useState('');

  // Generate QR code when needed
  React.useEffect(() => {
    if ((activeView === 'receive' || activeView === 'qr') && user?.walletAddress) {
      generateQRCode(user.walletAddress).then(setQrCodeUrl);
    }
  }, [activeView, user?.walletAddress]);

  // 재단 지갑을 기본값으로 설정
  React.useEffect(() => {
    if (isFoundationMode && !senderWallet) {
      // Vatton 재단 지갑 정보 설정
      const foundationWallet = {
        id: 'foundation',
        nickname: tb('foundationWallet'),
        username: 'Vatton Foundation',
        wallet_address: '0xFoundationWallet123456789',
        balance: 1000000, // 재단 지갑 기본 잔고
        is_active: true
      };
      setSenderWallet(foundationWallet);
      setSenderBalance(foundationWallet.balance);
    }
  }, [isFoundationMode, senderWallet]);

  // 실제 백엔드에서 토큰 데이터를 가져올 예정
  const tokens: Token[] = [
    {
      symbol: 'VTTN',
      name: 'Vatton Token',
      balance: user?.balance || 0,
      value: (user?.balance || 0) * 0.5,
      change: 0,
      icon: 'V'
    }
  ];

  const totalPortfolioValue = tokens.reduce((sum, token) => sum + token.value, 0);

  const handleSendTokens = async () => {
    if (!amount || !address) {
      toast.error('Please enter both amount and address.');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (transferAmount <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }

    if (transferAmount > user.balance) {
      toast.error('Insufficient balance.');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('http://159.223.63.184:3601/api/blockchain/vttn/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toAddress: address,
          amount: transferAmount,
          tokenType: 'VTTN'
        })
      });

      if (!response.ok) {
        throw new Error('전송 실패');
      }

      const result = await response.json();
      
      // 잔고 업데이트
      const newBalance = (user?.balance || 0) - transferAmount;
      await onUpdateBalance(newBalance);
      
      // 트랜잭션 추가
      const newTransaction = {
        id: Date.now(),
        type: 'Transfer Sent',
        amount: -transferAmount,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
      
      toast.success(`Successfully sent ${transferAmount} VTTN`);
      setAmount('');
      setAddress('');
      setActiveView('main');
    } catch (error) {
      console.error('전송 오류:', error);
      toast.error('Transfer failed.');
    } finally {
      setIsLoading(false);
    }
  };



  // 상단 Trans 버튼 클릭 시 토큰 전송 페이지로 이동
  const handleSendClick = () => {
    setActiveView('send');
  };

  // TransModal에서 전송 완료 시 호출
  const handleTransferComplete = (transferData: any) => {
    setShowTransModal(false);
    // 실제 전송 로직은 TransModal에서 처리
    toast.success(i18n.t('transferCompleted'));
  };

  // 전체 유저 목록 조회
  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('http://localhost:3600/api/vatton/users/all', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('유저 목록 조회 실패');
      }

      const users = await response.json();
      setAllUsers(users);
    } catch (error) {
      console.error('유저 목록 조회 오류:', error);
      toast.error(i18n.t('cannotLoadUsers'));
    }
  };

  // 재단 지갑으로 토큰 전송
  const handleFoundationTransfer = async () => {
    if (!foundationAmount) {
      toast.error(i18n.t('transferAmountRequired'));
      return;
    }

    if (!senderWallet) {
      toast.error(i18n.t('selectSenderWallet'));
      return;
    }

    if (!receiverWallet) {
      toast.error(i18n.t('selectReceiverWallet'));
      return;
    }

    const transferAmount = parseFloat(foundationAmount);
    if (transferAmount <= 0) {
      toast.error(i18n.t('enterValidAmount'));
      return;
    }

    // 잔고 확인
    if (senderBalance < transferAmount) {
      toast.error(i18n.t('insufficientFunds'));
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      // 개별 전송 API 사용
      const response = await fetch('http://localhost:3600/api/vatton/foundation/transfer-selected', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: transferAmount,
          tokenType: 'VTTN',
          userIds: [receiverWallet.id]
        })
      });

      if (!response.ok) {
        throw new Error('토큰 전송 실패');
      }

      const result = await response.json();
      
      toast.success(tr('transferSuccess', { amount: transferAmount, recipient: receiverWallet.nickname || receiverWallet.wallet_address }));
      
      // 잔고 업데이트
      setSenderBalance(prev => prev - transferAmount);
      setReceiverBalance(prev => prev + transferAmount);
      
      setFoundationAmount('');
      setActiveView('main');
    } catch (error) {
      console.error('재단 지갑 전송 오류:', error);
      toast.error(i18n.t('foundationTransferFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyWithUSDT = async () => {
    if (!swapAmount) {
      toast.error('Please enter amount to swap.');
      return;
    }

    const buyAmount = parseFloat(swapAmount);
    if (buyAmount <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('vatton-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('http://localhost:3600/api/vatton/swap/usdt-to-vttn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          usdtAmount: buyAmount,
          fromToken: 'USDT',
          toToken: 'VTTN'
        })
      });

      if (!response.ok) {
        throw new Error('구매 실패');
      }

      const result = await response.json();
      const vttnAmount = result.vttnAmount;
      
      // 잔고 업데이트
      const newBalance = (user?.balance || 0) + vttnAmount;
      await onUpdateBalance(newBalance);
      
      // 트랜잭션 추가
      const newTransaction = {
        id: Date.now(),
        type: 'VTTN Purchase',
        amount: vttnAmount,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
      
      toast.success(`Successfully purchased ${vttnAmount.toFixed(2)} VTTN with ${buyAmount} USDT`);
      setSwapAmount('');
      setActiveView('main');
    } catch (error) {
      console.error('구매 오류:', error);
      toast.error('Purchase failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(user?.walletAddress || '');
      toast.success('Address copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy address.');
    }
  };

  const handleWalletConnect = (walletName: string) => {
    setConnectedWallet(walletName);
    setShowWalletConnect(false);
  };

  const handleTokenClick = (token: Token) => {
    setSelectedToken(token);
    setActiveView('token-details');
  };

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleAddToken = (token: Token) => {
    setCustomTokens(prev => [...prev, token]);
  };

  // Combined tokens list
  const allTokens = [...tokens, ...customTokens];

  // Helper functions for swap functionality
  const swapTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setSwapAmount('');
  };

  const calculateSwapAmount = (inputAmount: string) => {
    if (!inputAmount) return '0.00';
    const input = parseFloat(inputAmount);
    if (fromToken === 'USDT' && toToken === 'VTTN') {
      return (input / 0.5).toFixed(2);
    } else if (fromToken === 'VTTN' && toToken === 'USDT') {
      return (input * 0.5).toFixed(2);
    }
    return '0.00';
  };

  // Generate QR code using qrcode library
  const generateQRCode = async (text: string) => {
    try {
      // Use dynamic import for QR code generation
      const QRCode = await import('qrcode');
      return await QRCode.toDataURL(text, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
    } catch (error) {
      console.warn('QR Code generation failed, using fallback:', error);
      // Fallback to a simple pattern without dynamic import
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      
      canvas.width = 200;
      canvas.height = 200;
      
      // Create a simple QR-like pattern
      const size = 8;
      const modules = 25;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 200, 200);
      
      ctx.fillStyle = '#000000';
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) - hash + text.charCodeAt(i)) & 0xffffffff;
      }
      
      for (let x = 0; x < modules; x++) {
        for (let y = 0; y < modules; y++) {
          const index = x * modules + y;
          if ((hash >> (index % 32)) & 1) {
            ctx.fillRect(x * size, y * size, size, size);
          }
        }
      }
      
      // Add finder patterns (corners)
      const drawFinderPattern = (startX: number, startY: number) => {
        ctx.fillStyle = '#000000';
        ctx.fillRect(startX, startY, 7 * size, 7 * size);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(startX + size, startY + size, 5 * size, 5 * size);
        ctx.fillStyle = '#000000';
        ctx.fillRect(startX + 2 * size, startY + 2 * size, 3 * size, 3 * size);
      };
      
      drawFinderPattern(0, 0);
      drawFinderPattern((modules - 7) * size, 0);
      drawFinderPattern(0, (modules - 7) * size);
      
      return canvas.toDataURL();
    }
  };

  // QR View
  if (activeView === 'qr') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">My QR Code</h1>
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
              <QrCode className="h-5 w-5 text-white" />
            </div>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <div className="relative mb-6">
                <div className="w-64 h-64 bg-white border-2 border-green-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                  {qrCodeUrl ? (
                    <img 
                      src={qrCodeUrl}
                      alt="Wallet Address QR Code"
                      className="w-60 h-60 rounded-xl"
                    />
                  ) : (
                    <div className="w-60 h-60 bg-gray-200 rounded-xl flex items-center justify-center">
                      <QrCode className="w-32 h-32 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-green-400 to-blue-400 rounded-full"></div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-4 mb-4">
                <p className="text-green-700 dark:text-green-300 font-medium mb-2">My Wallet Address</p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-700">
                  <p className="text-foreground text-sm font-mono break-all">
                    {user?.walletAddress || 'No wallet address'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Network</p>
                  <p className="text-foreground font-medium">Ethereum</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Balance</p>
                  <p className="text-green-600 font-medium">{(user?.balance || 0).toFixed(2)} VTTN</p>
                </div>
              </div>

              <Button
                onClick={copyAddress}
                className="w-full h-12 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Address
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Token Details View
  if (activeView === 'token-details' && selectedToken) {
    console.log('🎨 TokenDetails로 전달하는 transactions:', transactions);
    return (
      <TokenDetails
        token={selectedToken}
        transactions={transactions}
        onBack={() => setActiveView('main')}
        onSend={() => setActiveView('send')}
        onReceive={() => setActiveView('receive')}
        onBuy={() => setActiveView('buy')}
      />
    );
  }

  // Send View
  if (activeView === 'send') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Send Tokens</h1>
            <div className="w-10 h-10"></div>
          </div>

          <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
            <CardContent className="p-6 text-center">
              <p className="text-white/80 text-sm mb-1">Available Balance</p>
              <p className="text-3xl font-light">{(user?.balance || 0).toFixed(2)}</p>
              <p className="text-white/60 text-sm">VTTN</p>
            </CardContent>
          </Card>

          {/* 재단 지갑 모드 토글 */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground">{i18n.t('walletTransferTitle')}</h3>
                  <p className="text-sm text-muted-foreground">{i18n.t('walletTransferDesc')}</p>
                </div>
                <button
                  onClick={() => setIsFoundationMode(!isFoundationMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isFoundationMode ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isFoundationMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              {!isFoundationMode ? (
                <>
                  <div>
                    <Label htmlFor="address">Recipient Address</Label>
                    <Input
                      id="address"
                      placeholder="Enter wallet address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="amount">Amount (VTTN)</Label>
                    <Input
                      id="amount"
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>

                  <Button 
                    onClick={handleSendTokens}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-12"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    ) : (
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                    )}
                    {isLoading ? 'Sending...' : 'Send Tokens'}
                  </Button>
                </>
              ) : (
                <>
                  {/* 재단 지갑 전송 모드 UI - 원래 스타일로 복구 */}
                  <div>
                    <Label htmlFor="foundation-address">Recipient Address</Label>
                    <Input
                      id="foundation-address"
                      placeholder="Enter wallet address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="mt-1 border-border bg-input-background"
                    />
                  </div>

                  {/* 하단: 전송 금액 및 전송 버튼 */}
                  <div className="space-y-4 mt-6 pt-4 border-t">
                    <div>
                      <Label htmlFor="foundationAmount">{i18n.t('transferAmountLabel')}</Label>
                      <Input
                        id="foundationAmount"
                        type="number"
                        placeholder="0.00"
                        value={foundationAmount}
                        onChange={(e) => setFoundationAmount(e.target.value)}
                        className="mt-1 border-border bg-input-background"
                      />
                    </div>

                    <Button 
                      onClick={handleFoundationTransfer}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white h-12"
                      disabled={isLoading || !senderWallet || !receiverWallet || !foundationAmount}
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                      )}
                      {isLoading ? 'Sending...' : tr('transferFromTo', { from: senderWallet?.nickname || i18n.t('senderWallet'), to: receiverWallet?.nickname || i18n.t('receiverWallet') })}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 Send 화면 - transactions:', transactions?.length || 0);
                
                if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentTransactions')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500 mb-2">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-accent transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}...{tx.from?.substring(38)} → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()} | {i18n.t('blockLabel')} #{tx.blockNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${tx.to === user?.walletAddress ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.to === user?.walletAddress ? '+' : '-'}{parseFloat(tx.value).toFixed(2)} VTTN
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Receive View
  if (activeView === 'receive') {
    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Receive Tokens</h1>
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <ArrowDownLeft className="h-5 w-5 text-white" />
            </div>
          </div>

          <Card className="bg-card border-border border-purple-200 dark:border-purple-800">
            <CardContent className="p-6 text-center">
              <div className="relative mb-6">
                <div className="w-48 h-48 bg-white border-2 border-purple-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                  {qrCodeUrl ? (
                    <img 
                      src={qrCodeUrl}
                      alt="Wallet Address QR Code"
                      className="w-44 h-44 rounded-xl"
                    />
                  ) : (
                    <div className="w-44 h-44 bg-gray-200 rounded-xl flex items-center justify-center">
                      <QrCode className="w-24 h-24 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 mb-4">
                <p className="text-purple-700 dark:text-purple-300 font-medium mb-2">Your Wallet Address</p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                  <p className="text-foreground text-sm font-mono break-all">
                    {user?.walletAddress || 'No wallet address'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Network</p>
                  <p className="text-foreground font-medium">Ethereum</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="text-muted-foreground text-xs mb-1">Balance</p>
                  <p className="text-green-600 font-medium">{(user?.balance || 0).toFixed(2)} VTTN</p>
                </div>
              </div>

              <Button
                onClick={copyAddress}
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Address
              </Button>
            </CardContent>
          </Card>

          {/* Recent Receive History */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 UI 렌더링 - transactions:', transactions);
                console.log('🎨 UI 렌더링 - transactions.length:', transactions?.length);
                console.log('🎨 UI 렌더링 - Array.isArray:', Array.isArray(transactions));
                
                if (!transactions) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>⚠️ transactions = null</p>
                    </div>
                  );
                }
                
                if (!Array.isArray(transactions)) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>⚠️ transactions가 배열이 아닙니다</p>
                      <p className="text-xs">{typeof transactions}</p>
                    </div>
                  );
                }
                
                if (transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentReceives')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}...{tx.from?.substring(38)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()}
                          </p>
                          <p className="text-xs text-blue-500">{i18n.t('blockLabel')} #{tx.blockNumber}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-500">+{parseFloat(tx.value).toFixed(2)} VTTN</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Swap View (Uniswap style)
  if (activeView === 'buy') {

    return (
      <div className="h-full bg-background flex flex-col">
        <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setActiveView('main')}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">Swap Tokens</h1>
            <div className="w-10 h-10"></div>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-6 space-y-4">
              {/* From Token */}
              <div className="bg-muted rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-muted-foreground text-sm">From</p>
                  <p className="text-muted-foreground text-sm">Balance: {fromToken === 'USDT' ? '2,500.00' : (user?.balance || 0).toFixed(2)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Input
                    type="number"
                    placeholder="0.0"
                    value={swapAmount}
                    onChange={(e) => setSwapAmount(e.target.value)}
                    className="text-2xl font-bold bg-transparent border-0 p-0 h-auto"
                  />
                  <div className="flex items-center space-x-2 bg-background rounded-full px-3 py-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {fromToken === 'USDT' ? '₮' : 'V'}
                    </div>
                    <span className="font-bold">{fromToken}</span>
                  </div>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center">
                <button
                  onClick={swapTokens}
                  className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
                >
                  <RefreshCw className="w-6 h-6" />
                </button>
              </div>

              {/* To Token */}
              <div className="bg-muted rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-muted-foreground text-sm">To</p>
                  <p className="text-muted-foreground text-sm">Balance: {toToken === 'USDT' ? '2,500.00' : (user?.balance || 0).toFixed(2)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <p className="text-2xl font-bold">{calculateSwapAmount(swapAmount)}</p>
                  </div>
                  <div className="flex items-center space-x-2 bg-background rounded-full px-3 py-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {toToken === 'USDT' ? '₮' : 'V'}
                    </div>
                    <span className="font-bold">{toToken}</span>
                  </div>
                </div>
              </div>

              {/* Exchange Rate */}
              {swapAmount && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Exchange Rate</span>
                    <span className="font-bold">1 {fromToken} = {fromToken === 'USDT' ? '1.18' : '0.85'} {toToken}</span>
                  </div>
                </div>
              )}

              <Button 
                onClick={handleBuyWithUSDT}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white h-12"
                disabled={isLoading || !swapAmount}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Swapping...' : `Swap ${fromToken} for ${toToken}`}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Swap Transactions */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h4 className="font-bold text-foreground mb-3">Recent Transactions</h4>
              {(() => {
                console.log('🎨 Swap 화면 - transactions:', transactions?.length || 0);
                
                if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{i18n.t('noRecentSwaps')}</p>
                      <p className="text-sm">{i18n.t('transactionsWillAppear')}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-green-500 mb-2">{tr('transactionCount', { count: transactions.length })}</p>
                    {transactions.slice(0, 5).map((tx, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-accent transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {tx.from?.substring(0, 10)}... → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.timestamp * 1000).toLocaleString()} | {i18n.t('blockLabel')} #{tx.blockNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${tx.to === user?.walletAddress ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.to === user?.walletAddress ? '+' : '-'}{parseFloat(tx.value).toFixed(2)} VTTN
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main View (Wallet home)
  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Vatton Wallet</h1>
            <p className="text-muted-foreground text-sm">Secure Digital Asset Management</p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setShowWalletConnect(true)}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Link className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Portfolio Card */}
        <Card className="bg-gradient-to-br from-blue-600 via-purple-600 to-blue-500 border-0 text-white relative overflow-hidden">
          <CardContent className="p-6 relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-white/80 text-sm mb-2">Total Portfolio Value</p>
                <p className="text-4xl font-bold mb-1">
                  {balanceView === 'vttn' ? '' : ''}{balanceView === 'vttn' ? (user?.balance || 0).toFixed(2) : totalPortfolioValue.toFixed(2)}
                  {balanceView === 'vttn' ? ' VTTN' : ' USD'}
                </p>
                <p className="text-white/70 text-sm flex items-center">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  0% (24h)
                </p>
              </div>
              <button
                onClick={() => setBalanceView(balanceView === 'usd' ? 'vttn' : 'usd')}
                className="text-white/80 hover:text-white transition-colors p-2 rounded-full bg-white/10 hover:bg-white/20"
              >
                <Wallet className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div>
                <p className="text-white/70">VTTN Balance</p>
                <p className="text-white font-semibold">{(user?.balance || 0).toFixed(2)} VTTN</p>
              </div>
              <div className="text-right">
                <p className="text-white/70">USD Value</p>
                <p className="text-white font-semibold">${((user?.balance || 0) * 0.5).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
          
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
        </Card>

        {/* Action Buttons - 토큰 상세와 동일한 컬러 적용 */}
        <div className="grid grid-cols-4 gap-3">
          <Button
            onClick={() => setActiveView('send')}
            className="h-24 bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex flex-col items-center justify-center space-y-2 rounded-2xl shadow-lg"
          >
            <ArrowUpRight className="h-6 w-6 text-white" />
            <span className="text-xs font-medium text-white">Send</span>
          </Button>
          
          <Button
            onClick={() => setActiveView('receive')}
            className="h-24 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white flex flex-col items-center justify-center space-y-2 rounded-2xl shadow-lg"
          >
            <ArrowDownLeft className="h-6 w-6" />
            <span className="text-xs font-medium">Receive</span>
          </Button>
          
          <Button
            onClick={() => setActiveView('swap')}
            className="h-24 bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white flex flex-col items-center justify-center space-y-2 rounded-2xl shadow-lg"
          >
            <RefreshCw className="h-6 w-6" />
            <span className="text-xs font-medium">Swap</span>
          </Button>
          
          <Button
            onClick={() => setActiveView('qr')}
            className="h-24 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white flex flex-col items-center justify-center space-y-2 rounded-2xl shadow-lg"
          >
            <QrCode className="h-6 w-6" />
            <span className="text-xs font-medium">QR Code</span>
          </Button>
        </div>

        {/* Token List */}
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">My Assets</h3>
                  <p className="text-muted-foreground text-sm">{allTokens.length} tokens</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddToken(true)}
                className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-lg"
              >
                <Plus className="h-5 w-5 text-white" />
              </button>
            </div>

            <div className="space-y-4">
              {allTokens.map((token, index) => (
                <button
                  key={index}
                  onClick={() => handleTokenClick(token)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {token.icon}
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-foreground text-lg">{token.symbol}</p>
                      <p className="text-muted-foreground text-sm">{token.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground text-lg">{token.balance.toFixed(2)}</p>
                    <div className="flex items-center justify-end space-x-2">
                      <p className="text-muted-foreground text-sm">${token.value.toFixed(2)}</p>
                      <div className="flex items-center space-x-1">
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                          token.change >= 0 
                            ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' 
                            : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                        }`}>
                          {token.change >= 0 ? '+' : ''}{token.change.toFixed(2)}%
                        </span>
                        {token.change >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <WalletConnectModal
        isOpen={showWalletConnect}
        onClose={() => setShowWalletConnect(false)}
        onConnect={handleWalletConnect}
      />

      <AddTokenModal
        isOpen={showAddToken}
        onClose={() => setShowAddToken(false)}
        onAdd={handleAddToken}
      />

      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
      />



      <TransModal
        isOpen={showTransModal}
        onClose={() => setShowTransModal(false)}
        user={user}
        onUpdateBalance={onUpdateBalance}
        onAddTransaction={onAddTransaction}
      />
    </div>
  );
}